<div class="mx-auto flex max-w-4xl flex-col p-4">
  <div class="mb-4 h-8 w-8">
    <app-left-arrow-icon
      class="cursor-pointer"
      (click)="location.back()"
    ></app-left-arrow-icon>
  </div>

  <h1 class="mb-4 text-2xl font-bold">Edit License</h1>

  @if (errorFetchingLicense) {
    <div class="alert alert-error text-sm">
      <span>{{ errorFetchingLicense }}</span>
    </div>
  } @else if (loadingLicense) {
    <div class="overflow-hidden rounded-lg border border-base-content/20">
      <div class="skeleton h-12 w-full rounded-none"></div>
      <div class="space-y-4 p-4">
        <div class="skeleton h-4 w-1/3"></div>
        <div class="skeleton h-24 w-full"></div>
        <div class="skeleton h-4 w-1/2"></div>
        <div class="skeleton h-16 w-full"></div>
      </div>
    </div>
  } @else if (!originalLicense || !tenant) {
    <div class="alert alert-error text-sm">
      <span>No license or tenant data</span>
    </div>
  } @else {
    <!-- License overview -->
    <div class="overflow-hidden rounded-lg border border-base-content/20">
      <div
        class="flex flex-wrap items-start justify-between gap-3 border-b border-base-300/40 bg-base-300/10 px-4 py-3"
      >
        <div class="min-w-0">
          <div class="break-words text-lg font-semibold">
            {{ originalLicense.product.code }} -
            {{ originalLicense.product.title }} ({{
              originalLicense.product.variance_code
            }})
          </div>
          <div class="font-mono text-sm text-base-content/70">
            Account # {{ tenant.account_number }}
          </div>
        </div>
        <a
          class="link shrink-0 text-sm"
          [routerLink]="'/admin/license-change-history/' + originalLicense.id"
        >
          View change history
        </a>
      </div>

      <div class="grid grid-cols-1 gap-x-6 gap-y-4 px-4 py-4 sm:grid-cols-2">
        <div class="min-w-0">
          <div class="field-label">License Type</div>
          <div class="break-words text-base">
            {{ getLicenseType(originalLicense) }}
          </div>
        </div>

        <div class="min-w-0">
          <div class="field-label">Duration</div>
          <div class="break-words text-base">
            {{ originalLicense.product_variant.display_duration }}
          </div>
        </div>

        <div class="min-w-0">
          <div class="field-label">Price Lock Level</div>
          <div class="break-words text-base">
            {{ originalLicense.product_variant.price_lock_level }}
          </div>
        </div>

        <div class="min-w-0">
          <div class="field-label">Price</div>
          <div class="break-words text-base">
            {{ getLicensePrice(originalLicense) | currency: 'USD' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Editable settings -->
    <div class="mt-6 overflow-hidden rounded-lg border border-base-content/20">
      <div class="border-b border-base-300/40 bg-base-300/10 px-4 py-3">
        <div class="text-lg font-semibold">Settings</div>
        <div class="text-sm text-base-content/70">
          Changes apply when you save
        </div>
      </div>

      <div class="space-y-6 px-4 py-4">
        <div>
          <div class="field-label">Admin notes</div>
          <textarea
            class="textarea textarea-bordered mt-1 min-h-24 w-full"
            [(ngModel)]="inputNotes"
            placeholder="Internal notes (not visible to customers)"
          ></textarea>
        </div>

        <div
          class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <div class="min-w-0">
            <div class="field-label">Price Lock Level</div>
            @if (errorFetchingPriceLockLevels) {
              <p class="mt-1 text-sm text-error">
                {{ errorFetchingPriceLockLevels }}
              </p>
            } @else if (loadingPriceLockLevels) {
              <div class="loading loading-spinner loading-sm mt-2"></div>
            } @else if (priceLockLevels && priceLockLevels.length > 0) {
              <select
                class="select select-bordered mt-1 w-full"
                [(ngModel)]="updates.price_lock_level"
              >
                @for (level of priceLockLevels; track level.id) {
                  <option [ngValue]="level.price_lock_level">
                    {{ level.price_lock_level }} -
                    {{ getLockLevelPrice(level) | currency: 'USD' }}
                  </option>
                }
              </select>
            }
          </div>

          <div class="min-w-0">
            <div class="field-label">Start Date</div>
            <input
              type="date"
              class="input input-bordered mt-1 w-full"
              [(ngModel)]="inputStartDate"
            />
          </div>

          <div class="min-w-0">
            <div class="field-label">Expiration Date</div>
            <input
              type="date"
              class="input input-bordered mt-1 w-full"
              [(ngModel)]="inputExpirationDate"
            />
            <p class="mt-1 text-xs text-base-content/60">
              Changing this regenerates the activation key
            </p>
          </div>

          <div class="min-w-0">
            <div class="field-label">Transfers Left</div>
            <input
              type="number"
              class="input input-bordered mt-1 w-full"
              [(ngModel)]="updates.transfers_left"
            />
          </div>

          @if (
            errorFetchingAvailableParentLicenses ||
            loadingAvailableParentLicenses ||
            (availableParentLicenses && availableParentLicenses.length > 0)
          ) {
            <div class="min-w-0 sm:col-span-2">
              <div class="field-label">Parent License</div>
              @if (errorFetchingAvailableParentLicenses) {
                <p class="mt-1 text-sm text-error">
                  {{ errorFetchingAvailableParentLicenses }}
                </p>
              } @else if (loadingAvailableParentLicenses) {
                <div class="loading loading-spinner loading-sm mt-2"></div>
              } @else {
                <select
                  class="select select-bordered mt-1 w-full"
                  [(ngModel)]="updates.parent_desktop_license_id"
                >
                  <option value="">None</option>
                  @for (license of availableParentLicenses; track license.id) {
                    <option [ngValue]="license.id">
                      {{ license.product.code }} -
                      {{ license.product.title }} ({{
                        license.product.variance_code
                      }})
                    </option>
                  }
                </select>
              }
            </div>
          }

          <div class="min-w-0">
            <div class="field-label">Unique ID</div>
            <input
              type="text"
              class="input input-bordered mt-1 w-full font-mono"
              [(ngModel)]="inputUniqueId"
              placeholder="Machine unique ID"
            />
            <p class="mt-1 text-xs text-base-content/60">
              Changing this regenerates the activation key. Clearing it
              deactivates the current key.
            </p>
          </div>
        </div>

        <div>
          <!-- <div class="field-label">Flags</div> -->
          <div
            class="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-5"
          >
            <label
              class="flex cursor-pointer items-center gap-3 rounded-md border border-base-content/20 px-3 py-2"
            >
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                [(ngModel)]="updates.active"
              />
              <span class="text-sm">Active</span>
            </label>
            <label
              class="flex cursor-pointer items-center gap-3 rounded-md border border-base-content/20 px-3 py-2"
            >
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                [(ngModel)]="updates.site_license"
              />
              <span class="text-sm">Site License</span>
            </label>
            <label
              class="flex cursor-pointer items-center gap-3 rounded-md border border-base-content/20 px-3 py-2"
            >
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                [(ngModel)]="updates.renewal"
              />
              <span class="text-sm">Renewal</span>
            </label>
            <label
              class="flex cursor-pointer items-center gap-3 rounded-md border border-base-content/20 px-3 py-2"
            >
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                [(ngModel)]="updates.will_renew"
              />
              <span class="text-sm">Will Renew</span>
            </label>
            <label
              class="flex cursor-pointer items-center gap-3 rounded-md border border-base-content/20 px-3 py-2"
            >
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                [(ngModel)]="updates.new_license"
              />
              <span class="text-sm">New License</span>
            </label>
          </div>
        </div>
      </div>

      <div
        class="flex flex-wrap items-center justify-between gap-3 border-t border-base-300/40 bg-base-300/10 px-4 py-3"
      >
        <button
          type="button"
          class="btn btn-outline btn-error btn-sm"
          (click)="showConfirmDeleteDialog()"
        >
          Delete License
        </button>

        <div class="flex items-center gap-3">
          @if (updating) {
            <div class="loading loading-spinner loading-sm"></div>
          }
          <button
            type="button"
            class="btn btn-primary btn-sm"
            [disabled]="!updateChanges || updating"
            (click)="updateLicense()"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>

    @if (errorUpdating) {
      <div class="alert alert-error mt-4 text-sm">
        <span>{{ errorUpdating }}</span>
      </div>
    }
  }
</div>

<dialog class="modal" #deleteLicenseDialog>
  <div class="modal-box max-w-3xl p-8">
    <h3 class="text-lg font-bold">Confirm License Deletion</h3>

    <p class="mt-8 text-sm text-error">
      This deletion is permanent. Are you sure you want to delete this license?
    </p>

    <div class="mt-4 flex justify-end">
      @if (deletingLicense) {
        <div class="loading loading-spinner mr-4"></div>
      }
      <button
        class="btn btn-error mr-4"
        (click)="deleteLicense()"
        [disabled]="deletingLicense"
      >
        Delete
      </button>
      <button class="btn btn-neutral" (click)="closeDeleteLicenseDialog()">
        Cancel
      </button>
    </div>
  </div>
</dialog>
